<template>
  <div>
    <div v-if="!eventBlock.id" class="form-group">
      <label>How are events in this block triggered?</label>
      <div v-if="hasSources" class="radio">
        <label>
          <input v-model="eventBlock.type" type="radio" value="Source">
          A data source
        </label>
      </div>
      <div v-if="hasDateVariables" class="radio">
        <label>
          <input v-model="eventBlock.type" type="radio" value="Variable">
          A date variable
        </label>
      </div>
      <div class="radio">
        <label>
          <input v-model="eventBlock.type" type="radio" value="Manual">
          Manually/via API
        </label>
      </div>
      <div v-if="canCreateBirthdayBlock" class="radio">
        <label>
          <input v-model="eventBlock.type" type="radio" value="Birthday">
          On participants birthday
        </label>
      </div>
      <div class="radio">
        <label>
          <input v-model="eventBlock.type" type="radio" value="Date">
          On specific date(s)
        </label>
      </div>
    </div>

    <div v-if="showErrorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <div class="form-group">
      <label>Name*</label>
      <input v-model="eventBlock.name" type="text" class="form-control">
    </div>

    <div v-if="eventBlock.type === 'Source'" class="mb-4">
      <div class="form-group">
        <label>Select data source*</label>
        <select v-model="eventBlock.source_id" class="form-control" @change="resetCriteria">
          <option :value="null">
            No Source
          </option>
          <option v-for="source in sortedSourceOptions" :value="source.id">
            {{ source.name }}
          </option>
        </select>
      </div>
      <template v-if="eventBlock.source_id">
        <div class="form-group">
          <label>Select date field*</label>
          <select v-model="eventBlock.field_export_tag" class="form-control">
            <option :value="null" disabled>
              Select a field
            </option>
            <option v-for="(name) in dateFields" :value="name">
              {{ name }}
            </option>
            <option value="as_of">
              Timestamp of the event
            </option>
            <option value="created_at">
              Timestamp of date received in W2H
            </option>
          </select>
        </div>
        <div class="form-group form-inline ">
          <label>Reschedule events based on this trigger if new date is uploaded that matches</label>
          <select v-model="eventBlock.unique_export_tag" class="form-control form-control-sm">
            <option :value="null" disabled>
              Select a field
            </option>
            <option v-for="(field, id) in fields" :value="field.name">
              {{ field.name }}
            </option>
          </select>
        </div>
      </template>
      <div class="form-group form-inline ">
        <div class="checkbox">
          <label>
            <input v-model="eventBlock.ignore_overlap" type="checkbox"> If two unique instances of this block would overlap, schedule anyway
          </label>
        </div>
      </div>
      <div v-if="eventBlock.source_id && sourceHasFieldsetsForCriteria" class="form-group well">
        <label>Criteria</label><br>
        <small class="form-text">Use this date as a trigger only if</small>

        <div v-for="(criterion, idx) in eventBlock.criteria" :key="idx" class="mb-3">
          <strong v-if="idx >= 1" style="display: block; padding-left: 10px">AND</strong>

          <a class="btn btn-danger btn-sm float-right" @click.prevent="removeCriterion(eventBlock.criteria, idx)">
            <span class="fa fa-trash" />
            <span class="sr-only">Delete</span>
          </a>

          <Criterion :key="criterion.uuid" v-bind.sync="criterion" :sourceFieldsets="fieldsetsForSelectedSource" />
        </div>
        <div>
          <button class="btn btn-default btn-sm" @click.prevent="addCriterion()">
            <span class="fa fa-plus" />
            Add Condition
          </button>
        </div>
      </div>
    </div>

    <div v-if="eventBlock.type === 'Variable'" class="form-group">
      <label>Date Variable</label>
      <select v-model="eventBlock.variable_id" class="form-control form-control-sm">
        <option :value="null" disabled>
          Select a variable
        </option>
        <option v-for="(name, id) in variableOptions" :value="id">
          {{ name }}
        </option>
      </select>
    </div>

    <div v-if="eventBlock.type === 'Date'" class="form-group">
      <label>Date</label>
      <div class="list-unstyled text-nowrap">
        <div class="form-row mb-2" v-for="(date, i) in eventBlock.dates">
          <div class="form-group col-6">
            <b-form-datepicker
              v-model="date.date"
              placeholder="Select a date"
              :no-highlight-today="true"
              :min="tomorrow"
              @input="datePicked"
            />
          </div>

          <div class="form-group col-1">
            <a
                v-if="eventBlock.dates.length > 1"
                class="btn btn-danger btn-sm"
                @click="deleteDate(i)"

            >
              <span class="fa fa-trash" />
              <span class="sr-only">Delete</span>
            </a>
          </div>
        </div>
      </div>
      <div class="row">
        <label class="input-toggle col-sm-2">
          <a class="btn btn-default btn-sm" @click="addDate()">
            <span>Add Date</span>
          </a>
        </label>
      </div>
    </div>

    <div>
      <button
        class="btn btn-primary"
        :class="{'btn-loading': (isSubmitting), 'opacity-seventy': !isValid}"
        :disabled="!isValid || isSubmitting"
        @click="submit"
      >
        <span v-if="isSubmitting" class="glyphicon"/>Save
      </button>
      <a class="btn btn-link" v-bind="cancelDataAttrs" @click="resetFields">Cancel</a>
    </div>
  </div>
</template>

<script>
  import Vue from "vue";
  import { BFormDatepicker, TooltipPlugin } from 'bootstrap-vue';
  import {pickBy, orderBy, mapValues} from 'lodash-es';
  import 'bootstrap-vue/dist/bootstrap-vue.css';
  import Criterion from './EventBlockFormCriterion.vue';

  Vue.use(TooltipPlugin)

  export default {
    components: {
      Criterion,
      BFormDatepicker,
    },
    props: {
      defaultValues: {
        type: Object
      },
      sourceOptions: {
        type: Object
      },
      variableOptions: {
        type: Object
      },
      minDate: {
        type: String
      },
      postUrl: {
        type: String
      },
      canCreateBirthdayBlock: {
        type: Boolean
      },
      cancelDataAttrs: {
        type: Object
      }
    },
    data() {
      let dates = [
        {
          date: '',
          id: Math.random().toString(36).replace(/[^a-z]+/g, ''),
          isNew: true
        }
      ];
      if (this.defaultValues.dates) {
        dates = JSON.parse(this.defaultValues.dates);
        dates.forEach(function (date) {
          date.id = Math.random().toString(36).replace(/[^a-z]+/g, '');
          date.isNew = false;
        });
      }
      let defaultParams = {
        ...this.defaultValues,
        dates: dates,
        criteria: (this.defaultValues.criteria) ? JSON.parse(this.defaultValues.criteria) : []
      };

      if (defaultParams.source_trigger_type === 'as_of') {
        defaultParams.field_export_tag = 'as_of';
      }

      if (defaultParams.source_trigger_type === 'created_at') {
        defaultParams.field_export_tag = 'created_at';
      }

      return {
        eventBlock: defaultParams,
        isSubmitting: false,
        showErrorMessage: false,
        errorMessage: null
      }
    },
    computed: {
      tomorrow() {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
      },
      hasSources() {
        return Object.keys(this.sourceOptions).length > 0;
      },
      hasDateVariables() {
        return Object.keys(this.variableOptions).length > 0;
      },
      sortedSourceOptions() {
        // we have to add id to each object because we lose the key when we sort
        let sourcesWithId = mapValues(this.sourceOptions, (source, id) => { source.id = id; return source });
        return orderBy(sourcesWithId, [source => source.name.toLowerCase()], ['name', 'asc']);
      },
      fieldsetsForSelectedSource() {
        let fieldsets = this.option('fieldsets');
        return pickBy(fieldsets, (fieldset) => fieldset.type !== 'time');
      },
      fields() {
        return this.option('fields');
      },
      dateFields() {
        return this.option('dateFields');
      },
      sourceHasFieldsetsForCriteria() {
        return Object.keys(this.fieldsetsForSelectedSource).length > 0;
      },
      submissionFields() {
        return Object
          .getOwnPropertyNames(this.eventBlock)
          .reduce((map, prop) => {
            if (prop !== '__ob__') {
              switch (prop) {
                case 'dates':
                  if (this.eventBlock['dates']) {
                    let values = [];
                    this.eventBlock['dates'].forEach(date => {
                      if (date.date && date.date.length > 0) {
                        const dup = Object.assign({}, date);
                        let dateObj = new Date(date.date);
                        dup.date = dateObj.toISOString().split('T')[0]
                        delete dup.id;
                        delete dup.isNew;
                        values.push(dup);
                      }
                    });
                    values.sort(function (a, b) {
                      if (a.date < b.date) {
                        return -1;
                      }
                      if (a.date > b.date) {
                        return 1;
                      }
                      // a must be equal to b
                      return 0;
                    });

                    // De-duplicate the date array
                    values = values.filter((value, index, self) =>
                      index === self.findIndex((t) => (
                        t.date === value.date
                      ))
                    );

                    if (values.length) {
                      map[`event_block[dates]`] = JSON.stringify(values)
                    }
                  }
                  break;
                case 'field_export_tag':
                  if (this.eventBlock['field_export_tag']) {
                    if (['as_of', 'created_at'].includes(this.eventBlock['field_export_tag'])) {
                      map[`event_block[source_trigger_type]`] = this.eventBlock['field_export_tag'];
                      map[`event_block[field_export_tag]`] = null;
                    } else {
                      map['event_block[field_export_tag]'] = this.eventBlock['field_export_tag'];
                      map['event_block[source_trigger_type]'] = 'field';
                    }
                  }
                  break;
                case 'criteria':
                  map[`event_block[criteria]`] = JSON.stringify(this.eventBlock['criteria']);
                  break;
                case 'source_trigger_type':
                  break;
                case 'type':
                  if (this.eventBlock['type'] === 'Manual') {
                    map[`event_block[type]`] = 'Source';
                  } else {
                    map[`event_block[type]`] = this.eventBlock['type'];
                  }
                  break;
                default:
                  map[`event_block[${prop}]`] = this.eventBlock[prop];
              }
            }
            return map;
          }, {})
          ;
      },
    },
    watch: {
      'eventBlock.source_id': function(val, old) {
        if (val === null) {
          this.eventBlock.field_export_tag = null;
          this.eventBlock.unique_export_tag = null;
          this.eventBlock.criteria = [];
        }
      }
    },
    methods: {
      option: function (column) {
        return this.sourceOptions[this.eventBlock.source_id] ? this.sourceOptions[this.eventBlock.source_id][column] : [];
      },
      addCriterion: function () {
        if (!this.eventBlock.criteria) {
          this.eventBlock.criteria = [];
        }
        this.eventBlock.criteria.push({
          uuid: Math.random().toString(36).substr(2, 9), // unique id for key
          fieldset: "",
          operator: "",
          comparator: "",
          exportTag: "",
        });
      },
      datePicked: function(value) {
        let date = this.eventBlock.dates.find(date => date.date == value);
        date.isNew = true;
      },
      removeCriterion: function (list, idx) {
        list.splice(idx, 1);
      },
      resetCriteria: function () {
        this.eventBlock.criteria = [];
      },
      isValid() {
        if (!this.eventBlock.type) {
          this.errorMessage = "A trigger type must be selected";
          return false;
        }

        if (!this.eventBlock.name) {
          this.errorMessage = "A name is required";
          return false;
        }

        if (this.eventBlock.type === 'Source') {
          if (this.eventBlock.source_id) {
            if (!this.eventBlock.field_export_tag) {
              this.errorMessage = "A field must be selected";
              return false;
            }

            let error = false;

            this.$children.forEach(function (component) {
              if (component.hasOwnProperty('validate')) {
                if (component.validate()) {
                  error = true;
                }
              }

              return false;
            });

            if (error) {
              this.errorMessage = "Invalid criteria, please fix or remove.";
              return false;
            }
          }
        }

        if (this.eventBlock.type === 'Date') {
          if (
              !this.eventBlock.dates
              || this.eventBlock.dates.length === 0
              || this.eventBlock.dates[0].date.length === 0
          ) {
            this.errorMessage = "Date is required";
            return false;
          }

          for (const date of this.eventBlock.dates) {
            // New/modified dates can not be in the past, but existing ones can be
            if (date.isNew && new Date(date.date) < new Date()) {
              this.errorMessage = "Selected date can't be in the past";
              return false;
            }
          }
        }

        this.errorMessage = null;
        return true;
      },
      submit(e) {
        if (!this.isValid()) {
          this.showErrorMessage = true;
          return;
        }
        this.isSubmitting = true;

        jQuery.ajax({
          url: this.postUrl,
          type: 'POST',
          dataType: 'json',
          data: jQuery.param(this.submissionFields),
          success: (data) => {
            if (data.status === 'success') {
              this.$emit('success', data.block);
            } else {
              if (data.error && data.error === 'validation') {
                let errors = "There was an error: ";
                data.fields.forEach(function (err) {
                  if (typeof (err) === "string") {
                    errors += err + " ";
                  } else {
                    errors += err.error + " ";
                  }
                });
                this.showErrorMessage = true;
                this.errorMessage = errors
              }
              this.isSubmitting = false;
            }
          },
        })
      },
      resetFields() {
        Object.assign(this.$data, this.$options.data.call(this));
        this.$emit('cancel');
      },

      addDate() {
        this.eventBlock.dates.push({
          date: '',
          id: Math.random().toString(36).replace(/[^a-z]+/g, ''),
          isNew: true
        })
      },
      deleteDate(i) {
        this.eventBlock.dates.splice(i, 1);
      }
    }
  }
</script>
